// public/js/features/devices/devices-filter.js
// Collapsible filter panel for the devices table — mirrors features/assets/assets-filter.js.
// Filter keys map onto device fields (plus the special 'search' and 'assigned').
import { deviceTypeLabel } from './device-type.js';
import { isAllTenants } from '../../modules/tenant-scope.js';

export const DEVICE_FILTERS = [
  { key: 'search', label: 'Search', type: 'text', placeholder: 'Device ID, serial, IMEI…' },
  { key: 'type', label: 'Type', type: 'select', dynamic: 'types' },
  { key: 'tenantId', label: 'Tenant', type: 'select', dynamic: 'tenants' },
  {
    key: 'assigned', label: 'Assignment', type: 'select',
    options: [
      { value: 'yes', text: 'Assigned to asset' },
      { value: 'no', text: 'Unassigned' },
    ],
  },
  {
    // Ownership vs. asset assignment are different things: "ownerless" isolates
    // freshly-ingested devices that belong to no tenant yet — the ones a
    // platform role grants. Only meaningful when such devices are visible.
    key: 'owned', label: 'Ownership', type: 'select',
    options: [
      { value: 'yes', text: 'Owned by a tenant' },
      { value: 'no', text: 'Ownerless (grantable)' },
    ],
  },
];

// State survives navigation; reset on full HTML reload.
let filterState = {};

export function getFilterState() { return { ...filterState }; }
export function getActiveFilterCount() { return Object.keys(filterState).length; }
export function clearAllFilters() { filterState = {}; }

export function setFilterValue(key, value) {
  if (value === '' || value == null) delete filterState[key];
  else filterState[key] = value;
}

export function applyFilters(devices) {
  return devices.filter(d => {
    if (filterState.search) {
      const term = filterState.search.toLowerCase();
      const haystack = [d.deviceId, d.serialNumber, d.IMEI, d.SerNo, d.mac]
        .filter(Boolean).map(s => String(s).toLowerCase()).join(' ');
      if (!haystack.includes(term)) return false;
    }
    if (filterState.type && deviceTypeLabel(d) !== filterState.type) return false;
    if (filterState.tenantId && d.tenantId !== filterState.tenantId) return false;
    if (filterState.assigned === 'yes' && !d.assetUUID) return false;
    if (filterState.assigned === 'no' && d.assetUUID) return false;
    if (filterState.owned === 'yes' && !d.tenantId) return false;
    if (filterState.owned === 'no' && d.tenantId) return false;
    return true;
  });
}

// sources: { tenants: [{tenantId,name}], types: [{value,text}] }
export function renderFilterPanelHTML(sources = {}) {
  const { tenants = [], types = [] } = sources;

  // Global tenant scope: the per-page tenant (and ownership) filters only make
  // sense under "All tenants" — a single scope already pins the tenant. Drop
  // stale selections so they never filter invisibly.
  let filterList = DEVICE_FILTERS;
  if (!isAllTenants()) {
    filterList = filterList.filter(f => f.key !== 'tenantId' && f.key !== 'owned');
    delete filterState.tenantId;
    delete filterState.owned;
  }

  const rows = filterList.map(f => {
    const val = filterState[f.key] || '';
    let inputHtml = '';

    if (f.type === 'text') {
      inputHtml = `<input type="text"
        class="form-control form-control-sm device-filter-input"
        data-filter-key="${f.key}"
        placeholder="${f.placeholder || ''}"
        value="${String(val).replace(/"/g, '&quot;')}">`;
    } else {
      const source = f.dynamic === 'tenants'
        ? tenants.map(t => ({ value: t.tenantId, text: t.name || t.tenantId }))
        : f.dynamic === 'types'
          ? types
          : (f.options || []);
      const opts = [
        `<option value="">— All —</option>`,
        ...source.map(o => `<option value="${o.value}" ${val === o.value ? 'selected' : ''}>${o.text}</option>`),
      ].join('');
      inputHtml = `<select class="form-select form-select-sm device-filter-input${f.dynamic === 'tenants' ? ' tenant-blur' : ''}" data-filter-key="${f.key}">${opts}</select>`;
    }

    const hasClear = val !== '';
    return `
      <div class="row align-items-center mb-2 filter-row" data-filter-key="${f.key}">
        <label class="col-sm-3 col-md-2 col-form-label col-form-label-sm fw-semibold text-secondary">${f.label}</label>
        <div class="col-sm-9 col-md-5">
          <div class="input-group input-group-sm">
            ${inputHtml}
            <button class="btn btn-outline-secondary device-filter-clear ${hasClear ? '' : 'd-none'}"
                    data-filter-key="${f.key}" title="Clear">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
        </div>
      </div>`;
  }).join('');

  const activeCount = getActiveFilterCount();
  return `
    <div class="card shadow-sm mb-3" id="devices-filter-card">
      <div class="card-body py-2 px-3">
        <div class="d-flex justify-content-between align-items-center">
          <button class="btn btn-sm btn-link text-secondary text-decoration-none px-0 d-flex align-items-center gap-2"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#devices-filter-collapse"
                  aria-expanded="false"
                  aria-controls="devices-filter-collapse">
            <i class="fa-solid fa-filter"></i>
            <span class="fw-semibold">Filters</span>
            <span id="devices-filter-active-badge" class="badge bg-primary ${activeCount === 0 ? 'd-none' : ''}">${activeCount} active</span>
            <i class="fa-solid fa-chevron-down ms-1 device-filter-chevron" style="font-size:0.75rem; transition: transform 0.2s;"></i>
          </button>
          <button class="btn btn-sm btn-outline-secondary ${activeCount === 0 ? 'd-none' : ''}" id="btn-clear-all-device-filters">
            <i class="fa-solid fa-rotate-left me-1"></i>Clear all
          </button>
        </div>
        <div class="collapse" id="devices-filter-collapse">
          <div class="pt-3" id="devices-filter-rows">${rows}</div>
        </div>
      </div>
    </div>`;
}

export function attachFilterListeners(onFilterChange) {
  const updateBadgeAndClearAll = () => {
    const count = getActiveFilterCount();
    const badge = document.getElementById('devices-filter-active-badge');
    const clearAll = document.getElementById('btn-clear-all-device-filters');
    if (badge) { badge.textContent = `${count} active`; badge.classList.toggle('d-none', count === 0); }
    if (clearAll) { clearAll.classList.toggle('d-none', count === 0); }
  };

  document.querySelectorAll('input.device-filter-input').forEach(el => {
    let timer;
    el.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setFilterValue(el.dataset.filterKey, el.value.trim());
        el.closest('.input-group')?.querySelector('.device-filter-clear')
          ?.classList.toggle('d-none', !el.value.trim());
        updateBadgeAndClearAll();
        onFilterChange();
      }, 250);
    });
  });

  document.querySelectorAll('select.device-filter-input').forEach(el => {
    el.addEventListener('change', () => {
      setFilterValue(el.dataset.filterKey, el.value);
      el.closest('.input-group')?.querySelector('.device-filter-clear')
        ?.classList.toggle('d-none', !el.value);
      updateBadgeAndClearAll();
      onFilterChange();
    });
  });

  document.querySelectorAll('.device-filter-clear').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.filterKey;
      setFilterValue(key, '');
      const row = document.querySelector(`#devices-filter-rows .filter-row[data-filter-key="${key}"]`);
      if (row) { const inp = row.querySelector('.device-filter-input'); if (inp) inp.value = ''; }
      btn.classList.add('d-none');
      updateBadgeAndClearAll();
      onFilterChange();
    });
  });

  document.getElementById('btn-clear-all-device-filters')?.addEventListener('click', () => {
    clearAllFilters();
    document.querySelectorAll('.device-filter-input').forEach(el => { el.value = ''; });
    document.querySelectorAll('.device-filter-clear').forEach(b => b.classList.add('d-none'));
    updateBadgeAndClearAll();
    onFilterChange();
  });

  const collapseEl = document.getElementById('devices-filter-collapse');
  if (collapseEl) {
    collapseEl.addEventListener('show.bs.collapse', () => {
      document.querySelector('.device-filter-chevron')?.style.setProperty('transform', 'rotate(180deg)');
    });
    collapseEl.addEventListener('hide.bs.collapse', () => {
      document.querySelector('.device-filter-chevron')?.style.setProperty('transform', 'rotate(0deg)');
    });
  }
}
